import { error, ValidationError } from 'common/errors';
import { pushErrors } from 'common/gamp';
import { saveUser } from 'communicator/store';
import { getAccessToken, getUserCountry, getUserLocale } from 'communicator/store/selectors';
import { fetchReissuance } from 'communicator/utils/api/reissuance';
import { markEnd } from 'communicator/utils/instrumentation';
import { InitiateIdentityValidationPayload } from 'types/visa-sdk/initiate-identity-validation';

export default async function initiateIdentityValidationLegacy(): Promise<
  InitiateIdentityValidationPayload | ValidationError
> {
  try {
    const accessToken = getAccessToken();

    // If initiateIdentityValidation is called without an accessToken, e.g. when
    // idLookup returns NOTPRESENT, return an AUTH_ERROR.
    if (!accessToken?.token) {
      markEnd('INITIATE_IDENTITY_VALIDATION_LEGACY', {
        doFlush: true,
        failure: true,
        reason: 'AUTH_ERROR'
      });

      // START GENAI@GHCOPILOT
      pushErrors('INITIATE_IDENTITY_VALIDATION_LEGACY', 'AUTH_ERROR');
      // END GENAI@GHCOPILOT

      return error('AUTH_ERROR');
    }

    const reissuanceResponse = await fetchReissuance({
      accessToken: accessToken.token
    });

    const errorStatus = reissuanceResponse.ok
      ? reissuanceResponse.data.data.result?.status
      : reissuanceResponse.errorObject.data?.result.status;

    if (errorStatus) {
      if (errorStatus === 'UNKNOWN_ERROR') {
        markEnd('INITIATE_IDENTITY_VALIDATION_LEGACY', {
          doFlush: true,
          failure: true,
          reason: 'OTP_SEND_FAILED'
        });

        // START GENAI@GHCOPILOT
        pushErrors('INITIATE_IDENTITY_VALIDATION_LEGACY', 'OTP_SEND_FAILED');
        // END GENAI@GHCOPILOT

        return error('OTP_SEND_FAILED');
      }

      if (errorStatus === 'ACCT_INACCESSIBLE') {
        saveUser({
          accessToken: null
        });

        // START GENAI@GHCOPILOT
        pushErrors('INITIATE_IDENTITY_VALIDATION_LEGACY', 'ACCT_INACCESSIBLE');
        // END GENAI@GHCOPILOT
      }

      markEnd('INITIATE_IDENTITY_VALIDATION_LEGACY', {
        doFlush: true,
        failure: true,
        reason: errorStatus
      });

      return error(errorStatus);
    }

    // no errorStatus but API failed
    if (!reissuanceResponse.ok) {
      markEnd('INITIATE_IDENTITY_VALIDATION_LEGACY', {
        doFlush: true,
        failure: true,
        reason: 'OTP_SEND_FAILED'
      });

      return error('OTP_SEND_FAILED');
    }

    if (!reissuanceResponse.data.data.maskedValidationChannel) {
      markEnd('INITIATE_IDENTITY_VALIDATION_LEGACY', {
        doFlush: true,
        failure: true,
        reason: 'OTP_SEND_FAILED'
      });

      // START GENAI@GHCOPILOT
      pushErrors('INITIATE_IDENTITY_VALIDATION_LEGACY', 'OTP_SEND_FAILED');
      // END GENAI@GHCOPILOT

      return error('OTP_SEND_FAILED');
    }

    saveUser({
      accessToken: {
        expiresIn: reissuanceResponse.data.expires_in,
        isPartial: reissuanceResponse.data.auth_status === 'PARTIAL',
        token: reissuanceResponse.data.access_token
      }
    });

    markEnd('INITIATE_IDENTITY_VALIDATION_LEGACY');
    return {
      maskedValidationChannel:
        reissuanceResponse.data.data.maskedValidationChannel.maskedValidationChannel,
      profileCountry: getUserCountry(),
      profileLocale: getUserLocale()
    };
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const sErr = JSON.stringify(err) !== undefined ? JSON.stringify(err) : 'Unknown error';
    markEnd('INITIATE_IDENTITY_VALIDATION_LEGACY', { doFlush: true, failure: true, reason: sErr });
    pushErrors('Initiate Identity Validation Legacy Error', err);
    return error('UNKNOWN_FAILURE', sErr);
    // END GENAI@GHCOPILOT
  }
}
